import type {DownloadTicketingAttachmentResult, TicketingAttachment} from './types';
import type {AsyncThunk} from '@reduxjs/toolkit';

import {DeleteOutlined, DownloadOutlined, FileOutlined} from '@ant-design/icons';
import {IconButton, Loading} from '@ds/components';
import {useState} from 'react';
import {useAppDispatch} from 'store';

type DownloadAttachmentPayload = {
  id: string | number;
  vendor_id: string | number;
};

type AttachmentItemProps = {
  file: TicketingAttachment;
  vendorId: string | number;
  downloadAttachment: AsyncThunk<unknown, DownloadAttachmentPayload, object>;
  removable?: boolean;
  onRemove?: (file: TicketingAttachment) => void;
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const createAttachmentFilename = (fileExtension?: string): string => {
  const normalizedExtension =
    fileExtension && fileExtension.startsWith('.') ? fileExtension : `.${fileExtension ?? ''}`;

  return `SnappDownloadedFile_${Date.now()}${normalizedExtension === '.' ? '' : normalizedExtension}`;
};

const AttachmentItem: React.FC<AttachmentItemProps> = ({
  file,
  vendorId,
  downloadAttachment,
  removable = false,
  onRemove
}) => {
  const dispatch = useAppDispatch();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) {
      return;
    }

    setDownloading(true);
    try {
      const result = (await dispatch(
        downloadAttachment({id: file.id, vendor_id: vendorId})
      ).unwrap()) as DownloadTicketingAttachmentResult;
      const contentType = result.headers['content-type'] ?? 'application/octet-stream';
      const blob =
        result.data instanceof Blob ? result.data : new Blob([result.data], {type: contentType});
      downloadBlob(blob, createAttachmentFilename(file.file_ext));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2.5 rounded-xl border border-gray-03 p-1.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <FileOutlined aria-hidden className="shrink-0 text-body-xl text-gray-07" />
        <span className="truncate text-body-sm text-neutral-fg">
          {file.file_name || file.file_key || 'فایل پیوست'}
        </span>
      </div>

      {removable && onRemove ? (
        <IconButton
          aria-label="حذف فایل"
          variant="ghost"
          color="destructive"
          size="sm"
          onClick={() => onRemove(file)}
        >
          <DeleteOutlined aria-hidden />
        </IconButton>
      ) : (
        <IconButton
          aria-label="دانلود فایل"
          variant="ghost"
          color="secondary"
          size="sm"
          disabled={downloading}
          onClick={handleDownload}
        >
          {downloading ? (
            <Loading inline size="small" color="inherit" />
          ) : (
            <DownloadOutlined aria-hidden />
          )}
        </IconButton>
      )}
    </div>
  );
};

export default AttachmentItem;
